import React, { useEffect, useState } from 'react';
import { useOutliersData } from 'widgets/outliers/data/hooks';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { OutliersChart } from 'widgets/outliers/components/chart/OutliersChart';
import { DockingPanel } from '@instinet-analytics/semantic-ui-components';
import { BubbleMouseOver } from 'widgets/outliers/components/chart/BubbleMouseOver';
import { arraysDiffer } from 'utils/arrays/arrayUtils';
import { OutliersGridPanel } from 'widgets/outliers/components/grid/OutliersGridPanel';
import { OutliersChartSeries } from 'widgets/outliers/components/chart/series/outliersChartSeries';
import { Dimensions } from 'utils/types/Dimensions';
import { useOutlierWidgetDimensions } from 'widgets/outliers/components/hooks';

export interface OutliersContentProps {
    chartSeries: OutliersChartSeries;
    bubbleMouseOver: BubbleMouseOver;
    selectedOrderIds: string[];
    onSetSelectedOrderIds: (ids: string[]) => void;
    onPlotAreaDimensionsChanged: (nextDimensions: Dimensions) => void;
}
export function OutliersContent(props: OutliersContentProps) {
    const {
        chartSeries,
        selectedOrderIds,
        onSetSelectedOrderIds,
        bubbleMouseOver,
        onPlotAreaDimensionsChanged,
    } = props;
    const { config } = useOutliersConfigSlice();
    const { showGrid } = config;
    const {
        contentHeight,
        percentage,
        position,
        dockablePanelFirst: gridFirst,
        handlePercentageChange,
    } = useOutlierWidgetDimensions();

    const [highlightedOrderIds, setHighlightedOrderIds] = useState<string[]>(
        []
    );
    const [onlyShowSelected, setOnlyShowSelected] = useState(false);

    bubbleMouseOver.subscribe('OutliersContent', (e) => {
        setHighlightedOrderIds(e!.orderIds);
    });

    const data = useOutliersData();

    useEffect(() => {
        // Filter out any selected order ids not in the new data...
        if (data?.items && selectedOrderIds.length > 0) {
            const nextSelectedOrderIds = selectedOrderIds.filter(
                (s) => !!data.items.find((o) => o.orderID.toString() === s)
            );
            if (arraysDiffer(selectedOrderIds, nextSelectedOrderIds)) {
                onSetSelectedOrderIds(nextSelectedOrderIds);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.items]);

    const contentStyle = { height: contentHeight };

    if (!data?.items || data.items.length === 0) {
        return (
            <div className="OutliersContent" style={contentStyle}>
                No Data
            </div>
        );
    }

    return (
        <div className="OutliersContent" style={contentStyle}>
            <DockingPanel
                percentage={percentage}
                onPercentageChange={handlePercentageChange}
                position={position}
            >
                {gridFirst && showGrid && (
                    <OutliersGridPanel
                        data={data}
                        highlightedOrderIds={highlightedOrderIds}
                        selectedOrderIds={selectedOrderIds}
                        onSetSelectedOrderIds={onSetSelectedOrderIds}
                        onOnlyShowSelectedChanged={setOnlyShowSelected}
                        onlyShowSelected={onlyShowSelected}
                    />
                )}
                <OutliersChart
                    data={data}
                    bubbleMouseOver={bubbleMouseOver}
                    selectedOrderIds={selectedOrderIds}
                    onlyShowSelected={onlyShowSelected}
                    onSetSelectedOrderIds={onSetSelectedOrderIds}
                    chartSeries={chartSeries}
                    onPlotAreaDimensionsChanged={onPlotAreaDimensionsChanged}
                />
                {!gridFirst && showGrid && (
                    <OutliersGridPanel
                        data={data}
                        highlightedOrderIds={highlightedOrderIds}
                        selectedOrderIds={selectedOrderIds}
                        onSetSelectedOrderIds={onSetSelectedOrderIds}
                        onlyShowSelected={onlyShowSelected}
                        onOnlyShowSelectedChanged={setOnlyShowSelected}
                    />
                )}
            </DockingPanel>
        </div>
    );
}
